import React, { useContext, useEffect, useState } from "react";
import * as notify from "../../../shared/notify";
import classnames from "classnames";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faRandom, faStar, faCopy } from "@fortawesome/free-solid-svg-icons";
import EditImage from "./edit-image";
import { ShotContext } from "../context/index";
import { DEFAULT_LOCALE, CODING_SHOT } from "../lib/helpers";
import Gs1Modal from "./gs1-modal";
import { ShotControls } from "./shot-controls";
import UploadImage from "./upload-image";

let loadTimeout = null;

const ShotItem = ({ isPlaceholder, isCompleted, isFinalized, ...props }) => {
    const {
        imageViewCode,
        imageViewDescription,
        availableEditingSources,
        isRequired,
        isRotating,
        isUploading,
        key,
        editingSource,
        editingSourceId,
        uneditedBlobKey,
        copyInProgress,
        isPremiumEditing,
        isGs1MetadataEligible,
        gs1MetadataShortDescription,
        hasGs1Metadata,
        isMultipleImageEligible,
    } = props.view;
    const [rotation, setRotation] = useState(0);
    const [tempRotation, setTempRotation] = useState(0);
    const {
        handleSwap,
        packageTemplate,
        removePhoto,
        removeShot,
        rotateShot,
        selectedLocale,
        sourceShotKey,
        updateProperty,
        uploadImages,
        uploadZipFile,
        editingSources: defaultEditingSources,
        handleCopy,
        copySourceShot,
    } = useContext(ShotContext);

    useEffect(() => {
        return () => {
            clearTimeout(loadTimeout);
        };
    }, []);

    useEffect(() => {
        if (isUploading) {
            setRotation(0);
        }
    }, [isUploading]);

    useEffect(() => {
        if (rotation !== 0) {
            setRotation(0);
        }
    }, [uneditedBlobKey]);

    useEffect(() => {
        loadTimeout = setTimeout(async () => {
            if (tempRotation !== 0) {
                rotateShot(key, tempRotation);
                setTempRotation(0);
            }
        }, 450);
    }, [tempRotation]);

    const rotateImage = () => {
        if (isRotating || isUploading) {
            notify.error("Another action is in progress. Try again later.");

            return;
        }

        clearTimeout(loadTimeout);
        setRotation(rotation < 270 ? rotation + 90 : 0);
        setTempRotation(tempRotation < 270 ? tempRotation + 90 : 0);
    };

    const deleteShot = () => {
        if (isUploading || isRotating) {
            notify.error("Another action is in progress. Try again later.");

            return;
        }

        if (sourceShotKey && sourceShotKey === key) {
            notify.warning("Please deselect swap to delete");

            return;
        }

        if (!!uneditedBlobKey) {
            removePhoto(key, imageViewCode);
        } else {
            removeShot(imageViewCode);
        }
    };

    const upload = async (formData) => {
        if (isUploading || isRotating) {
            notify.error("Another action is in progress. Try again later.");

            return;
        }

        uploadImages(formData, imageViewCode);
    };

    const uploadZip = async (formData) => {
        if (isUploading) {
            notify.error("Another action is in progress. Try again later.");

            return;
        }

        uploadZipFile(formData, imageViewCode);
    };

    const changeProperty = (name, value) => {
        updateProperty([key], name, Number(value));
    };

    const handleSpecialAction = (event) => {
        if (isCompleted || isMultipleImageEligible) {
            return;
        }

        if (event.ctrlKey || event.metaKey) {
            return handleSwapClick(event);
        }

        if (event.altKey) {
            handleCopyClick(event);
        }
    };

    const handleSwapClick = () => {
        if (isUploading || isRotating) {
            notify.error("Another action is in progress. Try again later.");

            return;
        }

        if (!sourceShotKey && !key) {
            return;
        }

        handleSwap(props.view);
    };

    const handleCopyClick = (e) => {
        e.stopPropagation();

        if (isUploading || isRotating) {
            notify.error("Another action is in progress. Try again later.");

            return;
        }

        if (!sourceShotKey && !key) {
            // copy to empty placeholder
            if (copySourceShot && copySourceShot.key) {
                return handleCopy(props.view);
            }

            return;
        }

        handleCopy(props.view);
    };

    const customSources =
        availableEditingSources &&
        availableEditingSources.map((source) => {
            return {
                text: source.source,
                value: source.editingSourceId,
                isVisible: source.isVisibleInDropDown,
            };
        });

    const editingSourceOptions = customSources && customSources.length > 0 ? customSources : defaultEditingSources;

    const isCoding = !imageViewCode || imageViewCode === CODING_SHOT.imageViewCode;
    const isDefaultImageView =
        props.showEcom &&
        selectedLocale.value === DEFAULT_LOCALE.value &&
        packageTemplate.defaultImageView &&
        packageTemplate.defaultImageView === imageViewCode;
    const isEcomView = imageViewCode === "Ecom";
    const isSkipable = !isDefaultImageView && !isEcomView && !isPremiumEditing;
    const isShotUnremovable =
        isCoding && uneditedBlobKey && !isFinalized ? false : isCompleted || ((isDefaultImageView || isRequired) && !uneditedBlobKey);
    const canRemove = !(isShotUnremovable || isPlaceholder);
    const canUpload = !uneditedBlobKey && (!isCompleted || imageViewCode === CODING_SHOT.imageViewCode);

    let editingSourceFound = false;
    const useEditedSources = editingSourceOptions.reduce((arr, s) => {
        if (editingSourceId === s.value) {
            editingSourceFound = true;
        }

        if (!isSkipable && s.value === 4) {
            return arr;
        }

        arr.push(s);

        return arr;
    }, []);

    if (!editingSourceFound && !customSources) {
        useEditedSources.push({ text: editingSource, value: editingSourceId });
        editingSourceFound = false;
    }

    return (
        <div
            className={classnames("shot-item", {
                "-premium": isPremiumEditing,
                "-swap": sourceShotKey && key === sourceShotKey,
                "-copy": copySourceShot && key === copySourceShot.key,
            })}
        >
            <div
                className="image"
                onClick={handleSpecialAction}
            >
                <div className="overlay">
                    <FontAwesomeIcon icon={copySourceShot && copySourceShot.key ? faCopy : faRandom} />
                </div>
                {canUpload && (
                    <UploadImage
                        isRequired={!canRemove && !isCoding}
                        uploadImages={isMultipleImageEligible ? uploadZip : upload}
                        isUploading={isUploading || copyInProgress}
                        handleCopy={handleCopyClick}
                        zipUpload={isMultipleImageEligible}
                        multiple={isMultipleImageEligible}
                        isReadOnly={props.roUploadImage}
                    />
                )}
                {!!uneditedBlobKey && (
                    <EditImage
                        zipUpload={isMultipleImageEligible}
                        photo={props.view}
                        rotation={rotation}
                        wipKey={props.wipKey}
                        shotKey={key}
                        isCompleted={isCompleted}
                        isFinalized={isFinalized}
                        isCoding={isCoding}
                        showBulkSelect={!!uneditedBlobKey && !isCoding && !isCompleted && !props.roUploadImage}
                        isReadOnly={props.roUploadImage}
                    />
                )}
            </div>
            <h4>
                {imageViewDescription || CODING_SHOT.imageViewDescription} {isDefaultImageView ? " (Ecom)" : ""}
                {hasGs1Metadata && <div>{gs1MetadataShortDescription}</div>}
                {isPremiumEditing && (
                    <div className="premium-star">
                        <FontAwesomeIcon icon={faStar} />
                    </div>
                )}
            </h4>
            <ShotControls
                processing={copyInProgress || isRotating || isUploading}
                canChangeSource={!!uneditedBlobKey && !isCoding && !isCompleted}
                disableSourceSelect={isMultipleImageEligible || props.roSourceEdit}
                canSkip={isSkipable}
                canRemove={canRemove}
                canRotate={isMultipleImageEligible ? !isMultipleImageEligible : !!key}
                editedSources={useEditedSources}
                editingSourceId={editingSourceId}
                onChangeSource={changeProperty}
                onDeleteShot={deleteShot}
                onRotateImage={rotateImage}
                isVODelete={props.roDeleteImage}
            />
            {!!uneditedBlobKey && !!isGs1MetadataEligible && !isCompleted && (
                <Gs1Modal
                    image={props.view}
                    wipKey={props.wipKey}
                    metadata={hasGs1Metadata}
                />
            )}
        </div>
    );
};

export default ShotItem;
